import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Alert, Box, IconButton, Tooltip, Typography, useTheme } from "@mui/material";
import type { GridColumnVisibilityModel } from "@mui/x-data-grid";
import type { DateTime } from "luxon";
import { type FC, type ReactNode, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { PushReportSchedule } from "../../../components/PushReportSchedule/PushReportSchedule";
import { SystemDataGrid } from "../../../components/UIToolkit/Tables/SystemDataGrid";
import { frozenColumnsSx } from "../../../components/UIToolkit/Tables/SystemDataGrid/frozenColumnsSx";
import { opaqueHeaderTint, ROW_HOVER_BACKGROUND } from "../../../components/UIToolkit/Tables/SystemDataGrid/utils";
import { useAuth } from "../../../contexts/AuthProvider";
import { useViewWageAllowed } from "../../../hooks/useRevealWage";
import { managementCenterMultiGridKey } from "../../../services/LocalStorageService";
import { PushReportType } from "../../../utils/models";
import { buildMetricHeaderGroups } from "../../OpsBackoffice/metricColumnGroups";
import { migrateLegacyMultiFacilityGridStorage, readSavedGridColumnOrder } from "./gridStorage";
import { useMultiFacilityGrid, useNetworkWageAllowed } from "./hooks";
import {
	MULTI_FACILITY_GRID_DEFAULT_VISIBILITY,
	MULTI_FACILITY_GRID_FROZEN_FIELDS,
	MULTI_FACILITY_GRID_FROZEN_WIDTHS,
	type MultiFacilityGridRow,
	useMultiFacilityGridColumnPickerGroupLabels,
	useMultiFacilityGridColumnPickerGroups,
	useMultiFacilityGridColumns,
	useMultiFacilityGridExportableColumns,
	useMultiFacilityGridSortOptions,
} from "./multiFacilityGridColumns";
import { orderEmailedColumns } from "./utils";

/**
 * Default rows per page. 50 matches the network facility cap, so the largest real portfolio fits on
 * one page and the table reads as a whole network rather than a paged slice.
 */
const DEFAULT_PAGE_SIZE = 50;

type Props = {
	/** Every accessible facility — what the query loads, independent of what's on screen. */
	departmentIds: string[];
	/** The page-level selection: which of `departmentIds` the table actually shows. */
	visibleDepartmentIds: string[];
	sinceDate: DateTime;
	endDate?: DateTime;
	/** Drops one facility from the page-level selection (the row's own remove control). */
	onRemoveFacility: (departmentId: string) => void;
	/** Collapsed state is owned by the page so the heatmap can sit directly under a minimized table. */
	isMinimized: boolean;
	onToggleMinimized: () => void;
	/** Grid-view controls, rendered flush-right beside the column picker and export. */
	trailingNode: ReactNode;
	/** Anchors the emailed-report subscription — the facility whose network this grid is scoped to. */
	anchorDepartmentId: string;
};

/**
 * Network-level, backoffice-style metrics grid for the multi-facility ("All Facilities") view. Keeps
 * the two backoffice features head nurses need — the toolbar column picker and export — and drops
 * the ops-only ones (trends, ops-management editing, presets, and the network/ops-owner filters, since
 * this view is already scoped to a single network).
 *
 * Rows follow the page-level facility selection, and each row can drop itself from it, so the table is
 * a way to curate the comparison rather than a fixed list. The query still loads every accessible
 * facility (selection is never a query-key part), so removing and re-adding is instant and refetch-free.
 */
export const MultiFacilityGrid: FC<Props> = ({
	departmentIds,
	visibleDepartmentIds,
	sinceDate,
	endDate,
	onRemoveFacility,
	isMinimized,
	onToggleMinimized,
	trailingNode,
	anchorDepartmentId,
}) => {
	const { t } = useTranslation();
	const theme = useTheme();
	const { userInfo } = useAuth();
	// DEFAULT: "" is how the grid's own per-user storage hooks represent a not-yet-loaded user.
	const userId = userInfo?.id ?? "";
	// During render, not in an effect: the grid's storage hooks read on their first render and in their
	// user-id restore effects, and a child's effects run before its parent's — an effect here would land
	// after the grid had already read the (still empty) new key. Idempotent, so re-renders are harmless.
	migrateLegacyMultiFacilityGridStorage(userId);
	const { data, isLoading, isError } = useMultiFacilityGrid({ departmentIds, sinceDate, endDate });
	// Money columns always render now; the backend zeroes them per facility, and the restricted ones
	// are labelled in the cell. The only reason to drop the columns is a user who may not see wages
	// at all — which is what `useNetworkWageAllowed` answers (NOT the old portfolio-wide gate, where
	// one restricted facility hid PPD for the entire network).
	const wageAllowed = useNetworkWageAllowed();
	// From the RESPONSE, not the cached login snapshot: the endpoint reads live permissions, so a
	// permission change mid-session would otherwise leave the labels on the wrong rows.
	const wageRestrictedDepartmentIds = useMemo(() => new Set(data?.wageRestrictedDepartmentIds ?? []), [data]);
	const columns = useMultiFacilityGridColumns(wageAllowed, onRemoveFacility, wageRestrictedDepartmentIds);
	const columnPickerGroups = useMultiFacilityGridColumnPickerGroups(wageAllowed);
	const columnPickerGroupLabels = useMultiFacilityGridColumnPickerGroupLabels();
	const sortingOptions = useMultiFacilityGridSortOptions(wageAllowed);
	// The emailed report carries the columns this table is showing, in the order it shows them. The
	// picker's and reorder's models live in the grid (persisted per user in localStorage), so both are
	// reported back out — the cron that sends the email cannot read localStorage, so the resolved,
	// ordered list has to travel with the subscription.
	//
	// Gated on the PERMISSION, not `wageAllowed`: that one also folds in the reveal-wage toggle, which
	// is a screen-privacy convenience. Letting it decide here meant a permitted user with the toggle off
	// scheduled a report that permanently had no money columns.
	const wageViewAllowed = useViewWageAllowed();
	const exportableColumns = useMultiFacilityGridExportableColumns(wageViewAllowed);
	const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>(MULTI_FACILITY_GRID_DEFAULT_VISIBILITY);
	const [columnFieldOrder, setColumnFieldOrder] = useState<string[]>(() => readSavedGridColumnOrder(userId));
	const emailedColumns = useMemo(
		() => orderEmailedColumns(exportableColumns, columnVisibilityModel, columnFieldOrder),
		[exportableColumns, columnVisibilityModel, columnFieldOrder],
	);
	const emailScheduleButton = <PushReportSchedule departmentId={anchorDepartmentId} reportType={PushReportType.multi_facility_grid} columns={emailedColumns} />;
	const rows = useMemo(() => {
		const visible = new Set(visibleDepartmentIds);
		return (data?.departments ?? []).filter((row) => visible.has(row.departmentId));
	}, [data, visibleDepartmentIds]);
	// Two-tier grouped header band — same taxonomy/colors as the ops grid. The MC metric order is
	// band-contiguous, so every band renders as one merged cell by default; MUI still re-splits a band
	// by display order when a user drags a column out of its run.
	const headerGroups = useMemo(() => buildMetricHeaderGroups(t, theme), [t, theme]);
	const frozenSx = useMemo(
		() =>
			frozenColumnsSx(MULTI_FACILITY_GRID_FROZEN_FIELDS, MULTI_FACILITY_GRID_FROZEN_WIDTHS, {
				headerBackground: opaqueHeaderTint(theme),
				cellBackground: theme.palette.background.paper,
				cellHoverBackground: ROW_HOVER_BACKGROUND,
			}),
		[theme],
	);

	const minimizeButton = (
		<Tooltip title={isMinimized ? t("mc.multi.grid.expand") : t("mc.multi.grid.minimize")}>
			<IconButton size="small" onClick={onToggleMinimized} data-testid="multi-facility-grid-minimize">
				{isMinimized ? <ExpandMoreIcon fontSize="small" /> : <ExpandLessIcon fontSize="small" />}
			</IconButton>
		</Tooltip>
	);

	if (isError) {
		return (
			<Box sx={{ width: "100%" }}>
				<Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>{trailingNode}</Box>
				<Alert severity="error">{t("mc.multi.grid.error")}</Alert>
			</Box>
		);
	}

	const rangeLabel = `${sinceDate.toISODate()}_to_${endDate?.toISODate() ?? "today"}`;

	// Minimized: the grid unmounts and a compact summary bar keeps the controls (and the way back)
	// reachable. Collapsing rather than hiding means the heatmap below moves up into the space.
	if (isMinimized) {
		return (
			<Box sx={{ width: "100%" }}>
				<Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", py: 0.5 }} data-testid="multi-facility-grid-minimized-bar">
					{minimizeButton}
					{emailScheduleButton}
					<Typography variant="body2" sx={{ fontWeight: 700 }}>
						{t("mc.multi.grid.minimized.summary", { count: rows.length })}
					</Typography>
					<Box sx={{ ml: "auto" }}>{trailingNode}</Box>
				</Box>
			</Box>
		);
	}

	return (
		<Box sx={{ width: "100%" }}>
			<SystemDataGrid<MultiFacilityGridRow>
				rows={rows}
				columns={columns}
				loading={isLoading}
				getRowId={(row) => row.departmentId}
				localStorageKey={managementCenterMultiGridKey}
				sortingOptions={sortingOptions}
				enableColumnReorder
				onColumnOrderChange={setColumnFieldOrder}
				headerGroups={headerGroups}
				columnPicker={{
					defaultVisibility: MULTI_FACILITY_GRID_DEFAULT_VISIBILITY,
					onModelChange: setColumnVisibilityModel,
					syntheticColumns: columnPickerGroups,
					groupLabels: columnPickerGroupLabels,
					groupSelectAll: true,
				}}
				exportOptions={{ fileName: `multi_facility_grid_${rangeLabel}` }}
				leadingToolbarNodes={
					<>
						{minimizeButton}
						{emailScheduleButton}
					</>
				}
				trailingToolbarNodes={<Box sx={{ ml: "auto" }}>{trailingNode}</Box>}
				dataTestId="multi-facility-grid"
				sx={frozenSx}
				dataGridProps={{
					disableColumnMenu: true,
					// Column virtualization would unmount the frozen columns once scrolled far right.
					disableVirtualization: true,
					pagination: true,
					pageSizeOptions: [10, 20, 50, 100, { value: -1, label: t("All") }],
					initialState: { pagination: { paginationModel: { pageSize: DEFAULT_PAGE_SIZE } } },
				}}
			/>
		</Box>
	);
};
